import { feature, featureCollection } from "@turf/turf";
import { FeatureCollection } from "geojson";
import { useMemo } from "react";

import { GqlFieldworkerPreferredTransportationMethod, GqlPlannerTravelGroupOwnerBillingType } from "~/__generated__";
import { FIELDWORKER_COLORS } from "~/config/statusColors";
import GaiaMapDataLayer from "~/modules/map/components/GaiaMapDataLayer";
import { GaiaMapFeatureHoverListener } from "~/modules/map/components/GaiaMapEventListeners/GaiaMapFeatureHoverListener";
import { GaiaMapStyleCircle } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleCircle";
import { LAYER_DEFAULTS } from "~/modules/map/components/GaiaMapStyleComposites/layerDefaults";
import {
    IMG_MARKER_FIELDWORKER_BIKE,
    IMG_MARKER_FIELDWORKER_CAR,
    IMG_MARKER_FIELDWORKER_MYWHEELS,
    IMG_MARKER_FIELDWORKER_PICK_UP,
} from "~/modules/map/mapStyles";

import { WorkersLayerV2Fragment } from "./fragments.generated";

export type AvailableWorkerData = WorkersLayerV2Fragment & {
    preferredTransportationMethod: GqlFieldworkerPreferredTransportationMethod;
    maxShiftsPerWeek: number;
};

type AssignedWorkerData = WorkersLayerV2Fragment & {
    isOwner: boolean;
    ownerBillingType: GqlPlannerTravelGroupOwnerBillingType;
};

interface WorkerProperty {
    id: string;
    userId: string;
    user: {
        id: string;
        firstName: string;
    };
    isAssigned: boolean;
    icon: "pickup" | "bike" | "privateCar" | "myWheels";
}

const WORKERS_SOURCE_ID = "planner_workers_source";
const WORKERS_ICON_ID = "planner_workers_icon_layer";

export const WORKERS_MARKER_OFFSET: [number, number] = [-24, -25];

type WorkersLayerV2Props = {
    availableWorkers: AvailableWorkerData[];
    assignedWorkers: AssignedWorkerData[];
    onWorkerHover?: (workerId: string) => void;
};

function computeWorkerIcon(worker: {
    isOwner: boolean;
    ownerBillingType: GqlPlannerTravelGroupOwnerBillingType;
}): "pickup" | "bike" | "privateCar" | "myWheels" {
    if (!worker.isOwner) {
        return "pickup";
    }

    if (worker.ownerBillingType === "none") {
        return "bike";
    }

    return worker.ownerBillingType;
}

function useWorkersGeoJson(availableWorkers: AvailableWorkerData[], assignedWorkers: AssignedWorkerData[]): FeatureCollection {
    return useMemo(() => {
        const features = [
            ...(availableWorkers?.map(fieldworker => {
                const properties: WorkerProperty = {
                    id: fieldworker.id,
                    userId: fieldworker.userId,
                    user: fieldworker.user,
                    isAssigned: false,
                    icon: fieldworker.preferredTransportationMethod,
                };
                return feature(fieldworker.location, properties);
            }) ?? []),
            ...(assignedWorkers?.map(fieldworker => {
                const properties: WorkerProperty = {
                    id: fieldworker.id,
                    userId: fieldworker.userId,
                    user: fieldworker.user,
                    isAssigned: true,
                    icon: computeWorkerIcon(fieldworker),
                };
                return feature(fieldworker.location, properties);
            }) ?? []),
        ];
        return featureCollection(features);
    }, [availableWorkers, assignedWorkers]);
}

export function WorkersLayerV2({ availableWorkers, assignedWorkers, onWorkerHover }: WorkersLayerV2Props) {
    const workersGeoJson = useWorkersGeoJson(availableWorkers, assignedWorkers);

    return (
        <>
            <GaiaMapDataLayer id={WORKERS_SOURCE_ID} data={workersGeoJson} />

            <GaiaMapStyleCircle
                id={WORKERS_ICON_ID}
                source={WORKERS_SOURCE_ID}
                color={["case", ["==", ["get", "isAssigned"], true], FIELDWORKER_COLORS.assigned, FIELDWORKER_COLORS.available]}
                icon={{
                    color: LAYER_DEFAULTS.iconColor.onMarker,
                    image: [
                        "case",
                        ["==", ["get", "icon"], "pickup"],
                        IMG_MARKER_FIELDWORKER_PICK_UP,
                        ["==", ["get", "icon"], "myWheels"],
                        IMG_MARKER_FIELDWORKER_MYWHEELS,
                        ["==", ["get", "icon"], "privateCar"],
                        IMG_MARKER_FIELDWORKER_CAR,
                        IMG_MARKER_FIELDWORKER_BIKE,
                    ],
                }}
                label={{
                    textField: ["concat", ["get", "firstName", ["get", "user"]]],
                    color: "assigned",
                    filter: ["==", ["get", "isAssigned"], true],
                }}
            />

            <GaiaMapFeatureHoverListener
                onHoverCallback={feature => {
                    onWorkerHover?.(feature?.properties?.id);
                }}
                layerIds={[`${WORKERS_ICON_ID}_icon`]}
            />
        </>
    );
}
